import { useState, useEffect, useCallback } from "react";
import { InboxOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FilterOutlined, UploadOutlined, DatabaseOutlined, BarChartOutlined } from "@ant-design/icons";
import {
  Upload,
  Button,
  message,
  Table,
  Spin,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  UploadProps,
  TablePaginationConfig,
  Card,
  Space,
  Tag,
  Divider,
  Typography,
} from "antd";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import moment from "moment";

const { Title, Text } = Typography;

const { Dragger } = Upload;
const { Option } = Select;

// --- Type Definitions ---

/**
 * Type for an individual Price Appreciation entry.
 */
interface PriceAppreciation {
  date: string | Date; // ISO string or Date object from parsing/server
  price: number;
  _id?: string; // Optional, present in server response
}

/**
 * Type for a single Market Analysis record, both in parsed Excel data and fetched from API.
 */
interface MarketAnalysisRecord {
  // Fields from Excel/Form
  key?: string | number; // Required for Ant Design Table rows
  _id?: string; // MongoDB ID
  city: string;
  sectors: string;
  project: string;
  configuration: string;
  sizes: string;
  ticketSize: string;
  launchingRate: string;
  possession: string; // Could be a date string or Q/Year format string
  lastYear: string;
  last3year: string;
  last5year: string;
  priceAppreciation: PriceAppreciation[];
  userPostDataInfo?: {
    name: string;
    emailId: string;
    role: string;
    createdAt: string;
    updatedAt: string;
  }[];
}

/**
 * Type for the Price Appreciation data within the Ant Design Form, using moment/Dayjs objects.
 */
interface FormPriceAppreciation {
  date: dayjs.Dayjs; // Antd DatePicker uses moment/Dayjs object
  price: number;
}

/**
 * Type for the Form values on submission (for Update).
 */
interface FormValues extends Omit<MarketAnalysisRecord, 'priceAppreciation' | '_id' | 'key'> {
    // Override priceAppreciation for Form.List structure
    priceAppreciation?: FormPriceAppreciation[];
}

/**
 * Type for the Table parameters, specifically for Ant Design Table `onChange`.
 */
type AntdTableParams = {
    pagination: TablePaginationConfig;
    filters?: Record<string, (React.Key | null)[] | null>;
    sorter?: any; // Simplified for this context
};

/**
 * Type for the Ant Design Table Column.
 */
type RecordColumn = {
  title: string | React.ReactNode;
  dataIndex: keyof MarketAnalysisRecord | "index" | "actions";
  key: string;
  render?: (
    value: any,
    record: MarketAnalysisRecord,
    index: number
  ) => React.ReactNode;
};

// --- Component Start ---

const MarketAnalysis = () => {
  // Configure message globally
  useEffect(() => {
    message.config({
      top: 100,
      duration: 2,
      maxCount: 3,
    });

    // Add custom styles
    const style = document.createElement('style');
    style.innerHTML = `
      .market-table .ant-table-thead > tr > th {
        background: #1890ff !important;
        color: white !important;
        font-weight: 600 !important;
        padding: 12px 16px !important;
      }
      
      .market-table .ant-table-tbody > tr:hover > td {
        background: #f0f5ff !important;
      }
      
      .market-table .ant-pagination-item-active {
        border-color: #1890ff !important;
      }
      
      .market-table .ant-pagination-item-active a {
        color: #1890ff !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Use generic types for useState
  const [fileList, setFileList] = useState<any[]>([]);
  const [parsedData, setParsedData] = useState<MarketAnalysisRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [currentRecord, setCurrentRecord] = useState<MarketAnalysisRecord | null>(null);
  const [form] = Form.useForm<FormValues>();
  const [allRecords, setAllRecords] = useState<MarketAnalysisRecord[]>([]);
  const [fetchingRecords, setFetchingRecords] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Handle ResizeObserver error (robust, non-throwing suppression)

  
  // useEffect(() => {
  //   const handleError = (event: ErrorEvent) => {
  //     try {
  //       // Compose a message string from available properties safely
  //       const msg = (event && (event.message || (event.error && (event.error.message || event.error.toString())))) || '';
  //       if (typeof msg === 'string' && msg.includes('ResizeObserver')) {
  //         // Stop propagation if available (use as any to avoid TS issues)
  //         try {
  //           (event as any).stopImmediatePropagation?.();
  //         } catch (e) {}
  //         try { event.preventDefault?.(); } catch (e) {}
  //         // Log suppression for debugging (kept minimal)
  //         // eslint-disable-next-line no-console
  //         console.debug && console.debug('Suppressed ResizeObserver error');
  //       }
  //     } catch (err) {
  //       // Swallow any errors inside the handler to avoid creating new runtime errors
  //       // eslint-disable-next-line no-console
  //       console.debug && console.debug('Error in ResizeObserver suppression handler', err);
  //     }
  //   };

  //   const handleRejection = (ev: PromiseRejectionEvent) => {
  //     try {
  //       const reason = ev?.reason;
  //       const msg = reason && (reason.message || (typeof reason === 'string' ? reason : reason?.toString?.()));
  //       if (typeof msg === 'string' && msg.includes('ResizeObserver')) {
  //         try { ev.preventDefault?.(); } catch (e) {}
  //         // eslint-disable-next-line no-console
  //         console.debug && console.debug('Suppressed ResizeObserver promise rejection');
  //       }
  //     } catch (err) {
  //       // noop
  //     }
  //   };

  //   // Use capture phase and add safe handlers
  //   window.addEventListener('error', handleError, true);
  //   window.addEventListener('unhandledrejection', handleRejection, true);

  //   return () => {
  //     window.removeEventListener('error', handleError, true);
  //     window.removeEventListener('unhandledrejection', handleRejection, true);
  //   };
  // }, []);
  
  // Search and pagination states
  // Removed unused searchText and searchedColumn state
  const [filteredRecords, setFilteredRecords] = useState<MarketAnalysisRecord[]>([]);
  const [tableParams, setTableParams] = useState<AntdTableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "50", "100", "200", "500", "1000", "2000"],
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    },
  });

  // Fetch existing records - Use useCallback for stable function reference
  const fetchRecords = useCallback(async () => {
  setFetchingRecords(true);

  try {
    // ✅ Get user details from localStorage
    const storedUser = localStorage.getItem("userDetails");
    const userDetails = storedUser ? JSON.parse(storedUser) : null;

    const role = userDetails?.role;
    const userId = userDetails?.userId; // assuming your user object has _id

    if (!role) {
      message.error("User role not found. Please log in again.");
      setFetchingRecords(false);
      return;
    }

    // ✅ Base API URL
    let apiUrl = "https://service.ireedindia.com/v1/market-analysis";

    // ✅ Condition:
    // If role is NOT 'admin', send userId as query param
    if (role !== "admin" && userId) {
      apiUrl += `?userId=${userId}`;
    }

    console.log("🔗 Fetching from:", apiUrl);

    const response = await fetch(apiUrl);
    const result: { 
      success: boolean;
      message: string;
      data: MarketAnalysisRecord[] 
    } = await response.json();

    console.log("✅ Fetch result:", result);

    if (response.ok && (result.success || response.status === 200 || response.status === 304)) {
      const keyedData = result.data.map(item => ({
        ...item,
        key: item._id as string,
        sectors: item.sectors || 'N/A',
        project: item.project || 'N/A',
        city: item.city || 'N/A',
        configuration: item.configuration || 'N/A',
        sizes: item.sizes || 'N/A',
        ticketSize: item.ticketSize || 'N/A',
        launchingRate: item.launchingRate || 'N/A',
        possession: item.possession || 'N/A',
        lastYear: item.lastYear || '0%',
        last3year: item.last3year || '0%',
        last5year: item.last5year || '0%',
        priceAppreciation: item.priceAppreciation || [],
        userPostDataInfo: item.userPostDataInfo || [],
      }));

      setAllRecords(keyedData);
      setFilteredRecords(keyedData);

      setTimeout(() => {
        message.success({
          content: result.message || 'Records fetched successfully',
          duration: 2
        });
      }, 100);
    } else {
      setTimeout(() => {
        message.error({
          content: result.message || "Failed to fetch records",
          duration: 2
        });
      }, 100);
    }
  } catch (error) {
    console.error("❌ Error fetching records:", error);
    message.error("Failed to connect to server");
    alert("Failed to connect to server");
  } finally {
    setFetchingRecords(false);
  }
}, []);
// Empty dependency array means it's created once

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]); // Dependency on stable fetchRecords


  
  // Handle table change for pagination
  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      ...tableParams, // Maintain filters/sorter if they were ever used
      pagination,
    });
  };

  // Function to handle global search
  const handleGlobalSearch = (value: string | undefined) => {
    if (!value) {
      setFilteredRecords(allRecords);
      return;
    }

    const searchValue = value.toLowerCase();
    const filtered = allRecords.filter((record) => {
      // Safely access properties and check for search value
      return (
        (record.sectors && record.sectors.toLowerCase().includes(searchValue)) ||
        (record.project && record.project.toLowerCase().includes(searchValue)) ||
        (record.possession && record.possession.toLowerCase().includes(searchValue)) ||
        (record.configuration && record.configuration.toLowerCase().includes(searchValue)) ||
        (record.sizes && record.sizes.toLowerCase().includes(searchValue)) ||
        (record.ticketSize && record.ticketSize.toLowerCase().includes(searchValue)) ||
        (record.launchingRate && record.launchingRate.toLowerCase().includes(searchValue))
      );
    });

    setFilteredRecords(filtered);
    // Reset pagination to first page when searching
    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: 1,
      },
    }));
  };
  
  // --- Excel Parsing Logic ---
  
  const parseExcel = (file: File) => {
    setLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        // Ensure e.target.result is ArrayBuffer
        const data = new Uint8Array(e.target!.result as ArrayBuffer); 
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        // Use skipHidden: true if needed
        const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          throw new Error("Empty Excel file");
        }

        const headers = jsonData[0] as (string | undefined)[];

        // Flexible header finding with multiple possible variations
        const findColumnIndex = (possibleHeaders: string[]): number => {
          return headers.findIndex(
            (h) =>
              h &&
              typeof h === "string" &&
              possibleHeaders.some((ph) =>
                h.toLowerCase().includes(ph.toLowerCase())
              )
          );
        };

        // More flexible column finding
        const cityIdx = findColumnIndex(["city", "location", "City", "Location"]);
        const sectorIdx = findColumnIndex(["sector", "category", "Sector", "Sectors"]);
        const configurationIdx = findColumnIndex(["configuration", "config", "Configuration"]);
        const sizesIdx = findColumnIndex(["sizes", "size", "Sizes", "Sizes(Built up Area)"]);
        const ticketSizeIdx = findColumnIndex(["ticket size", "ticketSize", "Ticket Size", "Ticket Size (Cr)"]);
        const launchingRateIdx = findColumnIndex(["launching rate", "launchingRate", "Launching Rate"]);
        const projectIdx = findColumnIndex(["project", "name", "Project name"]);
        const possessionIdx = findColumnIndex([
          "possession", "possession date", "expected possession", "handover", "completion", "Possession"
        ]);
        const lastYearIdx = findColumnIndex(["last year", "Last Year ", "lastYear", "1 year", "yearly", "Last Year App."]);
        const last3YearIdx = findColumnIndex(["last 3", "last3year", "3 year", "3 years", "Last 3 year"]);
        const last5YearIdx = findColumnIndex(["last 5", "last5year", "5 year", "5 years", "Last 5 Year"]);

        const parsePercentage = (value: any): string => {
          if (value === undefined || value === null) return "0%";
        
          // If it's already a string with a percentage sign, return as-is
          if (typeof value === "string" && value.includes("%")) return value;
        
          // Clean and parse
          let strValue = value.toString().trim().replace(/%/g, "");
          const numValue = parseFloat(strValue);
        
          if (!isNaN(numValue)) {
            // Excel can store 0.15 for 15% or 15 for 15%. Handle both by capping at 1.0 (or 100)
            const finalValue = numValue > 100 ? numValue / 100 : numValue;
            const percentage = finalValue * 100;
        
            // Remove trailing zeroes after decimal
            return `${parseFloat(percentage.toFixed(2))}%`;
          }
        
          // Fallback, append % to the original value if parsing failed
          return value.toString() + "%";
        };
        
  // Read stored user once to attach to parsed preview rows
  const storedUserStr = localStorage.getItem("userDetails");
  const parsedStoredUser = storedUserStr ? JSON.parse(storedUserStr) : null;

  // Process data rows (skip header row)
        const formattedData: MarketAnalysisRecord[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row.length || !row[sectorIdx] && !row[projectIdx]) continue; // Skip empty rows or rows with no sector/project

          // --- Possession Date Parsing Logic ---
          let possessionDate: string = "N/A";
          if (possessionIdx !== -1 && row[possessionIdx]) {
            const rawValue = row[possessionIdx];
            
            if (typeof rawValue === 'number') {
              // Excel serial number
              try {
                // Use XLSX utility to convert serial number to Date object
                const possessionDateTime = XLSX.SSF.parse_date_code(rawValue);
                
                // Construct date string manually
                const year = possessionDateTime.y;
                // Fix the month offset - JavaScript months are 0-11, so we add 1 for display
                const month = String(possessionDateTime.m).padStart(2, '0');
                const day = String(possessionDateTime.d).padStart(2, '0');
                
                // Format in ISO format (YYYY-MM-DD)
                possessionDate = `${year}-${month}-${day}`;
              } catch (err) {
                console.error("Error converting Excel date:", err);
                possessionDate = rawValue.toString();
              }
            } else {
              // Handle string date formats
              const rawDate = rawValue.toString();
              
              let dayjsDate = dayjs(rawDate);
              
              if (!dayjsDate.isValid()) {
                const formats = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', 'MMM YYYY', 'MMMM YYYY', 'Q YYYY'];
                for (const format of formats) {
                  // NOTE: Dayjs handles 'Q YYYY' format (like 'Q4 2028') only if you have the quarterOfYear plugin, 
                  // or you use a custom parser. Since the original code has manual Quarter parsing, 
                  // we'll keep that for reliability if dayjs fails.
                  dayjsDate = dayjs(rawDate, format); 
                  if (dayjsDate.isValid()) break;
                }
              }
              
              if (dayjsDate.isValid()) {
                possessionDate = dayjsDate.format('YYYY-MM-DD');
              } else {
                // Manual Quarter parsing (from original code)
                if (rawDate.includes('Q')) {
                  const quarterMatch = rawDate.match(/Q(\d)\s*(\d{4})/i);
                  if (quarterMatch) {
                    const quarter = parseInt(quarterMatch[1]);
                    const year = parseInt(quarterMatch[2]);
                    const month = (quarter - 1) * 3; // 0-based month: Q1->0, Q2->3, Q3->6, Q4->9
                    
                    // Create a date object for the first day of that quarter's first month
                    const date = dayjs(new Date(year, month, 1)); 
                    possessionDate = date.format('YYYY-MM-DD');
                  } else {
                    possessionDate = rawDate; // Fallback to raw string
                  }
                } else {
                  possessionDate = rawDate; // Fallback to raw string
                }
              }
            }
          }
          // --- End Possession Date Parsing Logic ---

          // Create basic record with fallback values and percentage parsing
          const newRecord: MarketAnalysisRecord = {
            key: i, // Temporary key for preview table
            city: cityIdx !== -1 && row[cityIdx] ? row[cityIdx].toString() : "N/A",
            sectors:
              sectorIdx !== -1 && row[sectorIdx] ? row[sectorIdx].toString() : "N/A",
            project:
              projectIdx !== -1 && row[projectIdx] ? row[projectIdx].toString() : "N/A",
            configuration:
              configurationIdx !== -1 && row[configurationIdx] ? row[configurationIdx].toString() : "N/A",
            sizes:
              sizesIdx !== -1 && row[sizesIdx] ? row[sizesIdx].toString() : "N/A",
            ticketSize:
              ticketSizeIdx !== -1 && row[ticketSizeIdx] ? row[ticketSizeIdx].toString() : "N/A",
            launchingRate:
              launchingRateIdx !== -1 && row[launchingRateIdx] ? row[launchingRateIdx].toString() : "N/A",
            possession: possessionDate,
            lastYear: lastYearIdx !== -1 ? parsePercentage(row[lastYearIdx]) : "0%",
            last3year: last3YearIdx !== -1 ? parsePercentage(row[last3YearIdx]) : "0%",
            last5year: last5YearIdx !== -1 ? parsePercentage(row[last5YearIdx]) : "0%",
            priceAppreciation: [],
            userPostDataInfo: parsedStoredUser
              ? [
                  {
                    name:
                      parsedStoredUser.name || parsedStoredUser.username || parsedStoredUser.emailId || "",
                    emailId: parsedStoredUser.emailId || parsedStoredUser.email || "",
                    role: parsedStoredUser.role || "",
                    createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                    updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                  },
                ]
              : [],
          };
          
          // Find price columns (columns containing date/year and price information)
          const priceColumns: { index: number; date: Date; price: number }[] = [];
          headers.forEach((header, index) => {
            if (!header || typeof header !== "string") return;

            // Match patterns like "Apr 2024", "Current Rate Apr 2024", "Price 2024", etc.
            const monthYearMatch = header.match(
              /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)?\s*(\d{4})/i
            );

            if (monthYearMatch) {
              const monthStr = monthYearMatch[1] || "";
              const yearStr = monthYearMatch[2];

              // Convert month string to number (0-based)
              const monthNames = [
                "jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec",
              ];
              const monthIndex = monthStr
                ? monthNames.findIndex((m) =>
                    monthStr.toLowerCase().startsWith(m.toLowerCase())
                  )
                : 0;

              // Get the corresponding price value
              const price = Number(row[index]);

              if (!isNaN(price) && price > 0) {
                // Construct date for sorting (start of the month)
                priceColumns.push({
                  index,
                  date: new Date(parseInt(yearStr), monthIndex, 1),
                  price: price,
                });
              }
            }
          });

          // Sort price columns by date
          priceColumns.sort((a, b) => a.date.getTime() - b.date.getTime());

          // Add price appreciation data, converting Date to ISO string
          newRecord.priceAppreciation = priceColumns.map((col) => ({
            date: col.date.toISOString(),
            price: col.price,
          }));

          formattedData.push(newRecord);
        }

        setParsedData(formattedData);
        setLoading(false);

        if (formattedData.length > 0) {
          setTimeout(() => {
            message.success({
              content: `Successfully parsed ${formattedData.length} rows of data`,
              duration: 2,
              style: { marginTop: '20vh' }
            });
          }, 100);
        } else {
          setTimeout(() => {
            message.warning({
              content: "No valid data rows found in the Excel file",
              duration: 2,
              style: { marginTop: '20vh' }
            });
          }, 100);
        }
      } catch (error: any) {
        console.error("Error parsing Excel file:", error);
        message.error(`Failed to parse Excel file: ${error.message}`);
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // --- Upload Logic ---

  const handleUpload = async () => {
    if (parsedData.length === 0) {
      message.warning("No data to upload");
      return;
    }

    setUploading(true);

    try {
      let successCount = 0;
      let failureCount = 0;
      const userDetails = localStorage.getItem("userDetails");
      const storedUser = userDetails ? JSON.parse(userDetails) : null;
      for (const item of parsedData) {
        // Construct the payload as per the API structure
        const dataToSend = {
          city: item.city || "N/A",
          sectors: item.sectors || "Unknown Sector",
          project: item.project || `Project-${new Date().getTime()}-${successCount}`,
          possession: item.possession || "Not Specified",
          configuration: item.configuration || "Not Specified",
          userId:storedUser.userId,
          sizes: item.sizes || "Not Specified",
          ticketSize: item.ticketSize || "Not Specified",
          launchingRate: item.launchingRate || "Not Specified",
          lastYear: item.lastYear || "0%",
          last3year: item.last3year || "0%",
          last5year: item.last5year || "0%",
          priceAppreciation: Array.isArray(item.priceAppreciation)
            ? item.priceAppreciation.map((pa) => ({
                // pa.date is already an ISO string from parsing logic
                date: pa.date, 
                price: Number(pa.price),
              }))
            : [],
          // Attach user post info for server-side population & preview
          userPostDataInfo: storedUser
            ? [
                {
                  name: storedUser.name || storedUser.username || storedUser.emailId || "",
                  emailId: storedUser.emailId || storedUser.email || "",
                  role: storedUser.role || "",
                  createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                  updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
                },
              ]
            : [],
        };

        try {
          const response = await fetch("https://service.ireedindia.com/v1/market-analysis", { // Assuming this maps to https://service.ireedindia.com/v1/market-analysis/ on backend
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(dataToSend),
          });

          // The expected response status for success is 201 (Created) or 200 (OK)
          // Based on your original code's check for `result.status === 201`
          const result: { status: number; message: string; data?: MarketAnalysisRecord } = await response.json(); 

          if (response.ok && (result.status === 201 || response.status === 201)) {
            successCount++;
          } else {
            failureCount++;
            console.error(
              `Failed to upload ${dataToSend.project}:`,
              result.message
            );
            message.error(
              `Failed to upload ${dataToSend.project}: ${result.message}`
            );
          }
        } catch (error: any) {
          failureCount++;
          console.error(`Error uploading ${dataToSend.project}:`, error);
          message.error(
            `Error uploading ${dataToSend.project}: ${error.message}`
          );
        }
      }

      if (successCount > 0) {
        setTimeout(() => {
          message.success({
            content: `Successfully uploaded ${successCount} entries`,
            duration: 2
          });
        }, 100);

        if (failureCount > 0) {
          setTimeout(() => {
            message.warning({
              content: `${failureCount} entries failed to upload`,
              duration: 2
            });
          }, 200);
        }
        fetchRecords();
        setParsedData([]);
        setFileList([]);
      } else {
        setTimeout(() => {
          message.error({
            content: "All uploads failed",
            duration: 2
          });
        }, 100);
      }
    } catch (error: any) {
      console.error("Error in upload:", error);
      message.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // --- Edit/Delete Logic ---

  // Handle edit record
  const handleEdit = (record: MarketAnalysisRecord) => {
    setCurrentRecord(record);

    // Transform priceAppreciation from ISO string to moment object for Ant Design DatePicker
   const formattedPriceAppreciation: FormPriceAppreciation[] = record.priceAppreciation
  ? record.priceAppreciation.map((item) => ({
          date: dayjs(item.date),
          price: item.price,
        }))
  : [];

    form.setFieldsValue({
      ...record,
      priceAppreciation: formattedPriceAppreciation,
    } as FormValues); // Type assertion to match FormValues
    setIsEditModalVisible(true);
  };

  // Handle delete record
  const handleDelete = async (id: string) => {
    try {
      const response = await fetch(`https://service.ireedindia.com/v1/market-analysis/${id}`, {
        method: "DELETE",
      });
      const result = await response.json();

      if (result.success === true) {
        message.success("Record deleted successfully");
        // alert("Record deleted successfully");
        fetchRecords(); // Refresh the list
      } else {
        message.error(result.message || "Failed to delete record");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      message.error("Failed to connect to server");
    }
  };

  // Handle update record
  const handleUpdate = async (values: FormValues) => {
    if (!currentRecord || !currentRecord._id) {
        message.error("No record selected for update.");
        return;
    }
    try {
      // Read current stored user to preserve or populate userPostDataInfo if missing
      const _userStr = localStorage.getItem("userDetails");
      const _storedUser = _userStr ? JSON.parse(_userStr) : null;
      // Transform price appreciation data from moment back to ISO string
      const priceAppreciationArray: PriceAppreciation[] = (values.priceAppreciation || []).map(
        (item) => ({
          date: item.date ? item.date.toISOString() : new Date().toISOString(), // Convert moment to ISO string
          price: Number(item.price) || 0,
        })
      );

      // Create the updated record object, omitting 'key'
      const { key, ...restRecord } = currentRecord; 

      const updatedRecord = {
        ...restRecord,
        ...values,
        possession: values.possession || "Not Specified", // Ensure possession is a string
        priceAppreciation: priceAppreciationArray,
        // Since the form doesn't handle lastYear, last3year, last5year, they should be retained from the currentRecord
        lastYear: values.lastYear || currentRecord.lastYear,
        last3year: values.last3year || currentRecord.last3year,
        last5year: values.last5year || currentRecord.last5year,
      };

      // Ensure userPostDataInfo is preserved or set from stored user
      if (!updatedRecord.userPostDataInfo || updatedRecord.userPostDataInfo.length === 0) {
        if (currentRecord.userPostDataInfo && currentRecord.userPostDataInfo.length > 0) {
          updatedRecord.userPostDataInfo = currentRecord.userPostDataInfo;
        } else if (_storedUser) {
          updatedRecord.userPostDataInfo = [
            {
              name: _storedUser.name || _storedUser.username || _storedUser.emailId || "",
              emailId: _storedUser.emailId || _storedUser.email || "",
              role: _storedUser.role || "",
              createdAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
              updatedAt: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            },
          ];
        } else {
          updatedRecord.userPostDataInfo = [];
        }
      }

      const response = await fetch(`https://service.ireedindia.com/v1/market-analysis/${currentRecord._id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedRecord),
      });

      const result: { status: number; message: string } = await response.json();

      if (result.status === 200) {
        message.success("Record updated successfully");
        alert("Record updated successfully");
        setIsEditModalVisible(false);
        fetchRecords(); // Refresh the list
      } else {
        message.error(result.message || "Failed to update record");
      }
    } catch (error) {
      console.error("Error updating record:", error);
      message.error("Failed to connect to server");
    }
  };

  // Handle bulk delete (logic remains similar, but uses React.Key[])
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("No records selected");
      return;
    }

    setDeleting(true);
    try {
      let successCount = 0;
      let failureCount = 0;

      for (const key of selectedRowKeys) {
        const id = key as string; // Assert key as string (MongoDB _id)
        try {
          const response = await fetch(`https://service.ireedindia.com/v1/market-analysis/${id}`, {
            method: "DELETE",
          });
          const result: { status: number; message: string } = await response.json();

          if (result.status === 200) {
            successCount++;
          } else {
            failureCount++;
            console.error(`Failed to delete record ${id}:`, result.message);
          }
        } catch (error) {
          failureCount++;
          console.error(`Error deleting record ${id}:`, error);
        }
      }

      if (successCount > 0) {
        message.success(`Successfully deleted ${successCount} records`);
        if (failureCount > 0) {
          message.warning(`${failureCount} records failed to delete`);
        }
        setSelectedRowKeys([]);
        fetchRecords(); // Refresh the list
      } else {
        message.error("All delete operations failed");
      }
    } catch (error: any) {
      console.error("Error in bulk delete:", error);
      message.error(`Bulk delete failed: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Row selection config for Table component
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

const storedUser = localStorage.getItem("userDetails");
const userDetails = storedUser ? JSON.parse(storedUser) : null;
const role = userDetails?.role;
  // --- Table Columns ---

 const previewColumns: RecordColumn[] = [
  {
    title: "S.No",
    dataIndex: "index",
    key: "index",
    render: (_, __, index) => index + 1,
  },
  { 
    title: "City", 
    dataIndex: "city", 
    key: "city"
  },
  {
    title: "Sectors",
    dataIndex: "sectors",
    key: "sectors",
    render: (sectors: string) => {
      if (sectors && sectors.trim() !== "" && !sectors.toLowerCase().includes("sector")) {
        return `Sector ${sectors}`;
      }
      return sectors || "N/A";
    },
  },
  { 
    title: "Project Name", 
    dataIndex: "project", 
    key: "project"
  },
  {
    title: "Possession",
    dataIndex: "possession",
    key: "possession",
    render: (possession: string) => {
      if (possession && possession !== "N/A") {
        const date = dayjs(possession);
        if (date.isValid()) {
          return date.format("MMM, YYYY");
        }
      }
      return possession;
    },
  },
  { 
    title: "Configuration", 
    dataIndex: "configuration", 
    key: "configuration"
  },
  { 
    title: "Sizes", 
    dataIndex: "sizes", 
    key: "sizes" 
  },
  {
    title: "Ticket Size",
    dataIndex: "ticketSize",
    key: "ticketSize",
    render: (value: string | number) => {
      if (typeof value === "string") {
        const numMatch = value.match(/[\d.]+/);
        if (numMatch) {
          const num = parseFloat(numMatch[0]);
          if (!isNaN(num)) {
            if (value.toLowerCase().includes("cr")) return `${num.toFixed(2)} Cr`;
            if (value.toLowerCase().includes("lakh")) return `${num.toFixed(2)} Lakh`;
            if (num >= 10000000) {
              return `${(num / 10000000).toFixed(2)} Cr`;
            } else if (num >= 100000) {
              return `${(num / 100000).toFixed(2)} Lakh`;
            }
          }
        }
      }
      return value || "N/A";
    },
  },
  { 
    title: "Launching Rate", 
    dataIndex: "launchingRate", 
    key: "launchingRate" 
  },
  {
    title: "Price Appreciation",
    dataIndex: "priceAppreciation",
    key: "priceAppreciation",
    render: (priceAppreciation: FormPriceAppreciation[]) => {
      if (!priceAppreciation || priceAppreciation.length === 0) {
        return "N/A";
      }

      const formatted = priceAppreciation
        .map((item) => {
          const year = dayjs(item.date).format("YYYY");
          const price = `₹${item.price.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
          return `${year}: ${price}`;
        })
        .join(" | ");

      return formatted;
    },
  },
  { 
    title: "Last Year", 
    dataIndex: "lastYear", 
    key: "lastYear"
  },
  { 
    title: "Last 3 Years", 
    dataIndex: "last3year", 
    key: "last3year"
  },
  { 
    title: "Last 5 Years", 
    dataIndex: "last5year", 
    key: "last5year"
  },

  // ✅ Show only for admin users
  ...(role === "admin"
    ? [
        {
          title: "Created Date",
          dataIndex: "userPostDataInfo" as keyof MarketAnalysisRecord,
          key: "createdAt",
          render: (userPostDataInfo: MarketAnalysisRecord["userPostDataInfo"]) => {
            if (!userPostDataInfo || userPostDataInfo.length === 0) {
              return "N/A";
            }
            const user = userPostDataInfo[0];
            if (user.createdAt) {
              return dayjs(user.createdAt).format("DD MMM YYYY, hh:mm A");
            }
            return "N/A";
          },
        },
        {
          title: "Updated Date",
          dataIndex: "userPostDataInfo" as keyof MarketAnalysisRecord,
          key: "updatedAt",
          render: (userPostDataInfo: MarketAnalysisRecord["userPostDataInfo"]) => {
            if (!userPostDataInfo || userPostDataInfo.length === 0) {
              return "N/A";
            }
            const user = userPostDataInfo[0];
            if (user.updatedAt) {
              return dayjs(user.updatedAt).format("DD MMM YYYY, hh:mm A");
            }
            return "N/A";
          },
        },
        {
          title: "User Info",
          dataIndex: "userPostDataInfo" as keyof MarketAnalysisRecord,
          key: "userPostDataInfo",
          render: (userPostDataInfo: MarketAnalysisRecord["userPostDataInfo"]) => {
            if (!userPostDataInfo || userPostDataInfo.length === 0) {
              return "N/A";
            }

            const user = userPostDataInfo[0];
            return (
              <div>
                <div><strong>{user.name}</strong></div>
                <div style={{ fontSize: '12px', color: '#666' }}>{user.emailId}</div>
                <div style={{ fontSize: '12px', color: '#1890ff' }}>{user.role}</div>
              </div>
            );
          },
        },
      ]
    : []),
];

  const recordsColumns: RecordColumn[] = [
    ...previewColumns,
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          {role === "admin" && (
            <Button 
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                if (record._id && window.confirm("Are you sure you want to delete this record?")) {
                  handleDelete(record._id);
                }
              }} 
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Create filter options for sectors and cities
  const uniqueSectors = Array.from(new Set<string>(
    allRecords.map(item => typeof item.sectors === 'string' ? item.sectors : '')
  )).filter(Boolean); // Filter out empty strings/undefined

  const uniqueCities = Array.from(new Set<string>(
    allRecords.map(item => typeof item.city === 'string' ? item.city : '')
  )).filter(Boolean); // Filter out empty strings/undefined

  // --- Upload Props ---
  
  const uploadProps: UploadProps = {
    accept: ".xlsx, .xls",
    fileList,
    beforeUpload: (file: File) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      if (!isExcel) {
        setTimeout(() => {
          message.error({
            content: "You can only upload Excel files!",
            duration: 2,
            style: { marginTop: '20vh' }
          });
        }, 100);
        return Upload.LIST_IGNORE;
      }

      setFileList([file]);
      parseExcel(file);
      return false; // Prevent auto-upload
    },
    onRemove: () => {
      setFileList([]);
      setParsedData([]);
      return true;
    },
  };

  // --- Render ---

  return (
    <div className="p-6" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Market Analysis</h1>
          <p className="text-gray-600">Upload and manage your market analysis data</p>
        </div>

        {/* Upload Section */}
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Upload Excel File</h2>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: '16px', fontWeight: 500 }}>
              Click or drag Excel file to upload
            </p>
            <p className="ant-upload-hint" style={{ color: '#8c8c8c' }}>
              Support for Excel files (.xlsx, .xls) with market analysis data
            </p>
          </Dragger>
        </Card>

      {loading && (
        <Card className="text-center mb-6">
          <Spin size="large" />
          <p className="mt-4">Parsing Excel data...</p>
        </Card>
      )}

      {/* Preview Table */}
      {parsedData.length > 0 && (
        <Card className="mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">
              Preview Data <span className="text-gray-500">({parsedData.length} entries)</span>
            </h2>
          </div>
          
          <Table
            dataSource={parsedData} 
            columns={previewColumns}
            scroll={{ x: 'max-content' }}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
            }}
            className="market-table"
          />

          <div className="mt-4 text-right">
            <Button
              type="primary"
              size="large"
              icon={<UploadOutlined />}
              onClick={handleUpload}
              loading={uploading}
              disabled={parsedData.length === 0}
            >
              {uploading ? 'Uploading...' : 'Upload to Database'}
            </Button>
          </div>
        </Card>
      )}

     {/* Saved Records */}
      <Card>
        <div className="mb-4">
          <h2 className="text-xl font-semibold mb-4">Saved Records</h2>
          
          {/* Search and Filter */}
          <div className="mb-4 flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-3">
              <Input.Search
                placeholder="Search records..."
                onSearch={handleGlobalSearch}
                onChange={(e) => handleGlobalSearch(e.target.value)} 
                style={{ width: 250 }}
                allowClear
              />

              <Select
                placeholder="Filter by Sector"
                allowClear
                style={{ width: 180 }}
                onChange={(value: string | undefined) => {
                  if (!value) {
                    setFilteredRecords(allRecords);
                  } else {
                    setFilteredRecords(allRecords.filter(r => r.sectors === value));
                  }
                  setTableParams((prev) => ({
                    ...prev,
                    pagination: { ...prev.pagination, current: 1 },
                  }));
                }}
              >
                {uniqueSectors.map((sector) => (
                  <Option key={sector} value={sector}>{sector}</Option>
                ))}
              </Select>

              <Select
                placeholder="Filter by City"
                allowClear
                style={{ width: 180 }}
                onChange={(value: string | undefined) => {
                  if (!value) {
                    setFilteredRecords(allRecords);
                  } else {
                    setFilteredRecords(allRecords.filter(r => r.city === value));
                  }
                  setTableParams((prev) => ({
                    ...prev,
                    pagination: { ...prev.pagination, current: 1 },
                  }));
                }}
              >
                {uniqueCities.map((city) => (
                  <Option key={city} value={city}>{city}</Option>
                ))}
              </Select>
            </div>

            {selectedRowKeys.length > 0 && (
              <div className="flex gap-2 items-center">
                <span className="text-gray-600">{selectedRowKeys.length} selected</span>
                <Button 
                  danger 
                  loading={deleting} 
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm(`Delete ${selectedRowKeys.length} selected records?`)) {
                      handleBulkDelete();
                    }
                  }}
                >
                  Delete Selected
                </Button>
              </div>
            )}
          </div>
        </div>

        <Table
          rowSelection={rowSelection}
          dataSource={filteredRecords}
          columns={recordsColumns}
          pagination={tableParams.pagination}
          loading={fetchingRecords}
          onChange={handleTableChange as any} 
          className="market-table"
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Market Analysis Record"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form<FormValues> form={form} layout="vertical" onFinish={handleUpdate}>
          <Form.Item name="sectors" label="Sectors" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="city" label="City" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="configuration" label="Configuration" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="sizes" label="Sizes" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="ticketSize" label="Ticket Size" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="launchingRate" label="Launching Rate" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="project" label="Project" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          
          <Form.Item name="possession" label="Possession" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Divider>Price Appreciation</Divider>

          <Form.List name="priceAppreciation">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <Form.Item
                      {...restField}
                      name={[name, "date"]}
                      label="Date"
                      rules={[{ required: true }]}
                      style={{ flex: 1 }}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "price"]}
                      label="Price"
                      rules={[{ required: true }]}
                      style={{ flex: 1 }}
                    >
                      <Input type="number" min={0} />
                    </Form.Item>
                    <Button
                      danger
                      onClick={() => remove(name)}
                      style={{ marginTop: '30px' }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  + Add Price Entry
                </Button>
              </>
            )}
          </Form.List>

          <Divider>Appreciation Percentages</Divider>

          <Form.Item name="lastYear" label="Last Year" rules={[{ required: true }]}>
            <Input placeholder="e.g., 12%" />
          </Form.Item>
          
          <Form.Item name="last3year" label="Last 3 Years" rules={[{ required: true }]}>
            <Input placeholder="e.g., 35%" />
          </Form.Item>
          
          <Form.Item name="last5year" label="Last 5 Years" rules={[{ required: true }]}>
            <Input placeholder="e.g., 60%" />
          </Form.Item>
          
          <Form.Item>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsEditModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
      
      </div>
    </div>
  );
};

export default MarketAnalysis;